import { IDropzoneFile } from '@common/types/common';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    Box,
    Button,
    ButtonProps,
    SvgIconProps,
    SvgIconPropsSizeOverrides,
    Typography,
    TypographyProps,
} from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { toaster } from '@utils/common';
import clsx, { ClassValue } from 'clsx';
import { cloneElement, DetailedHTMLProps, ReactElement, useCallback } from 'react';
import { Accept, ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { defineMessage, useIntl } from 'react-intl';

type FileDropzoneAreaProps<F extends IDropzoneFile> = {
    title?: string | ReactElement;
    dragActiveTitle?: string | ReactElement;
    icon?: ReactElement | null;
    iconSize?: OverridableStringUnion<'small' | 'inherit' | 'large' | 'medium', SvgIconPropsSizeOverrides>;
    mainText?: string | ReactElement;
    subText?: string | ReactElement;
    containerProps?: DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    onDrop: (files: F[]) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
    fileFieldName?: string | number;
    onDropRejectMessage?: string;
    accept?: Accept;
    maxFiles?: number;
    multiple?: boolean;
    subtextClasses?: string;
    maxFileSize?: number; // In MB
    maxFileSizeInSubtext?: boolean;
    showButton?: boolean;
    buttonProps?: ButtonProps;
    buttonText?: string;
    buttonIcon?: ReactElement;
    buttonIconSize?: SvgIconProps['fontSize'];
    borderClasses?: ClassValue;
    dropzoneClasses?: ClassValue;
    titleProps?: TypographyProps;
    mainTextProps?: TypographyProps;
    subTextProps?: TypographyProps;
    disabled?: boolean;
};

const getErrorMessage = (code: string, maxFileSize: number) => {
    switch (code) {
        case ErrorCode.FileInvalidType:
            return defineMessage({ id: 'file.invalidType', defaultMessage: 'File type is not accepted' });
        case ErrorCode.FileTooLarge:
            return defineMessage({ id: 'file.tooLarge', defaultMessage: 'File is larger than {maxFileSize} MB' });
        case ErrorCode.FileTooSmall:
            return defineMessage({ id: 'file.tooSmall', defaultMessage: 'File is too small' });
        case ErrorCode.TooManyFiles:
            return defineMessage({ id: 'file.tooManyFiles', defaultMessage: 'Too many files' });
        default:
            return defineMessage({ id: 'file.unknownError', defaultMessage: 'Unknown error' });
    }
};

const FileDropzoneArea = <F extends IDropzoneFile>({
    title = 'Drop Files to Upload',
    dragActiveTitle = 'Drop Files here...',
    icon = <FileUploadOutlinedIcon />,
    iconSize = 'medium',
    mainText = 'Choose Files',
    onDropRejectMessage = 'There was an error adding your files',
    containerProps,
    accept = {
        'image/svg+xml': [],
        'image/png': [],
        'image/jpeg': [],
        'image/gif': [],
        'application/pdf': [],
    },
    maxFileSize = 3,
    subText = `${Object.keys(accept)
        .slice(0, -1)
        .map((type: string) => type.split('/')[1].split('+')[0].toLocaleUpperCase())
        .join(', ')} or ${Object.keys(accept).pop()?.split('/')[1].split('+')[0].toLocaleUpperCase()}`,
    subtextClasses,
    multiple,
    maxFiles,
    onDrop,
    onBlur,
    fileFieldName,
    buttonProps,
    showButton = false,
    maxFileSizeInSubtext = true,
    buttonText = 'Choose Files',
    buttonIcon = <UploadFileIcon />,
    buttonIconSize = 'medium',
    dropzoneClasses,
    titleProps,
    mainTextProps,
    subTextProps,
    disabled,
}: FileDropzoneAreaProps<F>) => {
    const intl = useIntl();
    const handleDrop = useCallback(
        (acceptedFiles: File[]) => {
            const newFiles: F[] = acceptedFiles.map((file) => ({ file, isLoading: false, error: '' } as F));
            onDrop(newFiles);
        },
        [onDrop]
    );

    const iconWithConfig = icon
        ? cloneElement<SvgIconProps>(icon as ReactElement<SvgIconProps>, { fontSize: iconSize })
        : null;
    const buttonIconWithConfig = cloneElement<SvgIconProps>(buttonIcon as ReactElement<SvgIconProps>, {
        fontSize: buttonIconSize,
    });

    const onDropRejected = (fileRejections: FileRejection[]) => {
        const messageMap: Record<string, string[]> = {};
        fileRejections.forEach((fr) => {
            const message = intl.formatMessage(getErrorMessage(fr.errors[0].code, maxFileSize), { maxFileSize });
            if (!messageMap[message]) messageMap[message] = [];
            messageMap[message].push(fr.file.name);
        });
        toaster.failure({
            msg: (
                <>
                    <ul>
                        {Object.keys(messageMap).map((mmk) => {
                            messageMap[mmk] = messageMap[mmk].sort((a, b) => a.length - b.length);
                            return (
                                <li key={mmk} className="leading-4">
                                    {messageMap[mmk].map((fn, i) => (
                                        <>
                                            {fn}
                                            {i < messageMap[mmk].length - 1 && (
                                                <>
                                                    ;<br />
                                                </>
                                            )}
                                        </>
                                    ))}
                                    : <br />
                                    {mmk}
                                    <br />
                                    <br />
                                </li>
                            );
                        })}
                    </ul>
                </>
            ),
            title: onDropRejectMessage,
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        onDropRejected,
        accept,
        multiple,
        maxFiles,
        maxSize: maxFileSize * Math.pow(1024, 2),
        onFileDialogCancel: () => onBlur?.({ target: { name: fileFieldName, value: [] } } as any),
        disabled,
    });

    return (
        <div
            {...containerProps}
            className={clsx(containerProps?.className, 'rounded border-black/12 border-2 border-dashed', {
                'opacity-50 pointer-events-none': disabled,
            })}
        >
            <div
                {...getRootProps({
                    className: clsx(
                        'relative overflow-hidden rounded-sm flex flex-col items-center justify-center h-full ring-spanish-blue text-auro-metal-saurus py-20 md:min-w-80 cursor-pointer',
                        { 'ring-2 ring-spanish-blue/80': isDragActive },
                        dropzoneClasses
                    ),
                })}
            >
                <input {...getInputProps({ multiple, style: { display: 'none' } })} />
                <Box sx={{ color: (theme) => theme.palette['neutral-800'].main }} className="text-center w-full">
                    <div className="flex flex-col gap-5 items-center">
                        {iconWithConfig}
                        <div className="text-center">
                            <Typography className="hidden md:block" fontWeight={700} variant="h5" {...titleProps}>
                                {isDragActive ? dragActiveTitle : title}
                            </Typography>
                            <Typography className="md:hidden block" fontWeight={700} variant="h5" {...mainTextProps}>
                                {mainText}
                            </Typography>
                            <Typography className={subtextClasses} {...subTextProps}>
                                {subText}
                                {maxFileSizeInSubtext && ` (max. ${maxFileSize}MB)`}
                            </Typography>
                            {showButton && (
                                <Button
                                    {...buttonProps}
                                    startIcon={buttonIconWithConfig}
                                    className={clsx('mt-10', buttonProps?.className, 'rounded-sm')}
                                >
                                    {buttonText}
                                </Button>
                            )}
                        </div>
                    </div>
                </Box>
            </div>
        </div>
    );
};

export { FileDropzoneArea };
export type { FileDropzoneAreaProps };
